import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { QRBlock } from "@/components/QRCode";
import { NameTagPrinter } from "@/components/badges/NameTagPrinter";
import { PrivacyPromise } from "@/components/privacy/PrivacyPromise";
import { EmptyState, SectionLoader } from "@/components/common";
import { formatDateLong } from "@/lib/format";
import { Plus, Ticket, CalendarDays, Users2, Loader2, Ban, QrCode, IdCard } from "lucide-react";

const origin = () => (typeof window !== "undefined" ? window.location.origin : "");

function InvitesTab() {
  const [invites, setInvites] = useState(null);
  const [label, setLabel] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/invites");
      setInvites(res.data);
    } catch (e) {
      toast.error("Could not load invites");
      setInvites([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setCreating(true);
    try {
      await api.post("/invites", { label: label.trim() || null });
      setLabel("");
      toast.success("Invite created");
      await load();
    } catch (e) {
      toast.error("Could not create invite");
    } finally {
      setCreating(false);
    }
  };

  const deactivate = async (id) => {
    try {
      await api.delete(`/invites/${id}`);
      toast.success("Invite deactivated");
      await load();
    } catch (e) {
      toast.error("Could not deactivate invite");
    }
  };

  if (invites === null) return <SectionLoader label="Loading invites…" />;

  return (
    <div className="space-y-6">
      <Card className="border-border bg-card">
        <CardContent className="p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <Label htmlFor="invite-label">New invite label (optional)</Label>
              <Input id="invite-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Coffee meetup · table 3" className="mt-1.5" data-testid="invite-label-input" />
              <p className="mt-1 text-xs text-muted-foreground">Labels help you track which QR brought in each new member.</p>
            </div>
            <Button onClick={create} disabled={creating} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="invite-create-button">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1.5 h-4 w-4" /> Create invite</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {invites.length === 0 ? (
        <EmptyState icon={Ticket} title="No invites yet" description="Create an invite to generate a QR code that opens the signup page. Signups will be attributed to you." testId="invites-empty" />
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {invites.map((inv) => {
            const url = `${origin()}/register?invite=${inv.invite_id}`;
            return (
              <Card key={inv.invite_id} className={`border-border bg-card ${!inv.active ? "opacity-60" : ""}`} data-testid="invite-card">
                <CardContent className="flex flex-col items-center p-5">
                  <div className="mb-2 flex w-full items-center justify-between gap-2">
                    <p className="line-clamp-1 text-sm font-semibold text-foreground" title={inv.label || "Untitled invite"}>{inv.label || "Untitled invite"}</p>
                    {inv.active ? (
                      <Badge variant="secondary" className="shrink-0"><Users2 className="mr-1 h-3 w-3" />{inv.signup_count} joined</Badge>
                    ) : (
                      <Badge variant="secondary" className="shrink-0 bg-stone-100 text-stone-700">Deactivated</Badge>
                    )}
                  </div>
                  <QRBlock value={url} size={150} fileName={`invite-${inv.invite_id}`} printTitle="Join Friday Conversations" printSubtitle={inv.label || "Scan to create your member account"} testId={`invite-qr-${inv.invite_id}`} />
                  {inv.active && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="mt-3 text-muted-foreground hover:text-destructive" data-testid="invite-deactivate"><Ban className="mr-1.5 h-4 w-4" />Deactivate</Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Deactivate this invite?</AlertDialogTitle>
                          <AlertDialogDescription>The QR code will stop working for new signups. Existing members are unaffected.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deactivate(inv.invite_id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="invite-deactivate-confirm">Deactivate</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EventsTab() {
  const [events, setEvents] = useState(null);

  useEffect(() => {
    api.get("/events/admin/all")
      .then((r) => setEvents(r.data))
      .catch(() => { toast.error("Could not load events"); setEvents([]); });
  }, []);

  if (events === null) return <SectionLoader label="Loading events…" />;

  if (events.length === 0) {
    return <EmptyState icon={CalendarDays} title="No events yet" description="Create an event first, then come back to generate a shareable QR code for it." testId="event-qr-empty" action={<Button asChild><Link to="/admin/manage/events">Create an event</Link></Button>} />;
  }

  return (
    <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
      {events.map((ev) => {
        const url = `${origin()}/events/${ev.event_id}`;
        return (
          <Card key={ev.event_id} className="border-border bg-card" data-testid="event-qr-card">
            <CardContent className="flex flex-col items-center p-5">
              <div className="mb-2 w-full text-center">
                <p className="line-clamp-1 text-sm font-semibold text-foreground" title={ev.title}>{ev.title}</p>
                <p className="text-xs text-muted-foreground">{ev.date ? formatDateLong(ev.date) : "Date TBA"}</p>
              </div>
              <QRBlock value={url} size={150} fileName={`event-${ev.event_id}`} printTitle={ev.title} printSubtitle={ev.date ? formatDateLong(ev.date) : "Friday Conversations"} testId={`event-qr-${ev.event_id}`} />
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function BadgesTab() {
  return <NameTagPrinter />;
}

export default function AdminQR() {
  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-serif text-2xl font-semibold text-foreground sm:text-3xl">QR Codes</h1>
        <p className="mt-1 text-muted-foreground">Generate, download and print QR codes for onboarding new members and sharing events.</p>
      </div>

      <PrivacyPromise />

      <Tabs defaultValue="invites">
        <TabsList>
          <TabsTrigger value="invites" data-testid="qr-tab-invites"><Ticket className="mr-1.5 h-4 w-4" />Invites</TabsTrigger>
          <TabsTrigger value="events" data-testid="qr-tab-events"><QrCode className="mr-1.5 h-4 w-4" />Events</TabsTrigger>
          <TabsTrigger value="badges" data-testid="qr-tab-badges"><IdCard className="mr-1.5 h-4 w-4" />Name badges</TabsTrigger>
        </TabsList>
        <TabsContent value="invites" className="mt-6"><InvitesTab /></TabsContent>
        <TabsContent value="events" className="mt-6"><EventsTab /></TabsContent>
        <TabsContent value="badges" className="mt-6"><BadgesTab /></TabsContent>
      </Tabs>
    </div>
  );
}
